import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCreateAirbnbConnectionLink,
  useTestConnection,
  useUpsertChannelMapping,
  useActivateChannel,
  useDisableChannel,
  useSyncChannel,
  airbnbListingsQuery,
  channelConnectionsByProviderQuery,
  apartmentChannelEventsQuery,
} from '@/lib/channels';
import type { ApartmentChannelMapping, ChannelConnection } from '@/lib/channels';
import type { AirbnbChannelSettings } from '@/api/generated/types.gen';
import { messageFromError } from '@/lib/api';
import { env } from '@/lib/env';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ReadOnlyValue } from './ReadOnlyValue';

const MIN_STAY_TYPE_OPTIONS = [
  { value: 'Arrival', label: 'Arrival' },
  { value: 'Through', label: 'Through' },
];

const BOOKING_AMOUNT_OPTIONS = [
  { value: 'Payout Amount', label: 'Payout Amount' },
  { value: 'Total Paid Amount', label: 'Total Paid Amount' },
];

const POLL_INTERVAL_MS = 3_000;
const MAX_POLL_MS = 120_000;

interface Props {
  apartmentID: string;
  mapping: ApartmentChannelMapping | undefined;
  canWrite: boolean;
}

export function AirbnbConnect({ apartmentID, mapping, canWrite }: Props) {
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const { data: connections = [] } = useQuery(
    channelConnectionsByProviderQuery('airbnb'),
  );
  // Connected Airbnb accounts the operator can select, map and activate. Channex
  // creates a freshly-connected channel INACTIVE, so 'inactive' must be included
  // — otherwise the just-connected account never matches the poll and is hidden
  // from the dropdown (dead-end). 'pending_auth'/'error' are excluded (no channel yet).
  const connectedConnections = connections.filter(
    (c) => c.status === 'active' || c.status === 'inactive',
  );

  const linkedConnId = mapping?.connection_id;
  // If linkedConnId is set but not in connectedConnections (e.g. that connection was deleted),
  // leave selection empty rather than silently auto-selecting a different account.
  const linkedConnExists = connectedConnections.some((c) => c.id === linkedConnId);
  const defaultConnId =
    linkedConnId && linkedConnExists
      ? linkedConnId
      : connectedConnections[0]?.id ?? '';

  const [selectedConnId, setSelectedConnId] = useState<string>(defaultConnId);
  const [selectedListingId, setSelectedListingId] = useState<string>(
    mapping?.external_listing_id ?? '',
  );
  const [polling, setPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [pendingConnId, setPendingConnId] = useState<string | null>(null);

  // "Connect new account" channel settings (AirbnbChannelSettings) — collected up
  // front so the Airbnb channel is never created with unreviewed Channex defaults.
  const [showSettings, setShowSettings] = useState(false);
  const [minStayType, setMinStayType] =
    useState<NonNullable<AirbnbChannelSettings['min_stay_type']>>('Arrival');
  const [bookingAmountSettings, setBookingAmountSettings] =
    useState<NonNullable<AirbnbChannelSettings['booking_amount_settings']>>('Payout Amount');
  const [cohostPayoutCalculations, setCohostPayoutCalculations] = useState(false);
  const [sendEmailNotifications, setSendEmailNotifications] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);

  // Keep selected conn in sync when connections load.
  // Only auto-select if nothing is currently selected (don't override an explicit user choice).
  useEffect(() => {
    if (!selectedConnId && connectedConnections.length > 0) {
      setSelectedConnId(connectedConnections[0]?.id ?? '');
    }
  }, [connectedConnections, selectedConnId]);

  const createLink = useCreateAirbnbConnectionLink();
  const testConn = useTestConnection();
  const upsertMapping = useUpsertChannelMapping(apartmentID);
  const activate = useActivateChannel(apartmentID);
  const disable = useDisableChannel(apartmentID);
  const sync = useSyncChannel(apartmentID);

  const { data: listings = [] } = useQuery({
    ...airbnbListingsQuery(selectedConnId),
    enabled: !!selectedConnId,
  });

  const { data: events = [] } = useQuery(
    apartmentChannelEventsQuery(apartmentID, 'airbnb'),
  );
  const recentErrors = events.filter((e) => e.severity === 'error').slice(0, 5);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  function stopPolling() {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }

  function startPolling() {
    setPolling(true);
    setPollTimedOut(false);
    pollStartRef.current = Date.now();

    pollRef.current = setInterval(() => {
      const elapsed = Date.now() - pollStartRef.current;
      if (elapsed >= MAX_POLL_MS) {
        stopPolling();
        setPollTimedOut(true);
        return;
      }
      void qc.invalidateQueries({ queryKey: ['channel-connections'] });
    }, POLL_INTERVAL_MS);
  }

  // When polling, stop as soon as the specific connection we created (pendingConnId)
  // appears as connected (active|inactive — the callback lands it inactive).
  // Matching by exact id is safe regardless of server sort order.
  useEffect(() => {
    if (!polling || !pendingConnId) return;
    const found = connectedConnections.find((c) => c.id === pendingConnId);
    if (found) {
      setSelectedConnId(found.id);
      setPendingConnId(null);
      stopPolling();
    }
     
  }, [connectedConnections, polling, pendingConnId]);

  async function handleConnectNew() {
    setErr(null);
    if (sendEmailNotifications && !notificationEmail.trim()) {
      setEmailError('Email is required when email notifications are enabled.');
      return;
    }
    setEmailError(null);

    const settings: AirbnbChannelSettings = {
      min_stay_type: minStayType,
      booking_amount_settings: bookingAmountSettings,
      cohost_payout_calculations: cohostPayoutCalculations,
      send_email_notifications: sendEmailNotifications,
    };
    if (sendEmailNotifications) {
      settings.email = notificationEmail.trim();
    }

    try {
      const result = await createLink.mutateAsync({
        label: 'Airbnb',
        anchor_apartment_id: apartmentID,
        redirect_uri: `${env.apiOrigin}/api/v1/channel-connections/airbnb/callback`,
        settings,
      });
      const { connection_id, redirect_url } = result.data;
      setPendingConnId(connection_id);
      window.open(redirect_url, '_blank');
      startPolling();
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleMap() {
    setErr(null);
    if (!selectedConnId) {
      setErr('Select an Airbnb account first.');
      return;
    }
    if (!selectedListingId) {
      setErr('Select a listing to map.');
      return;
    }
    try {
      await upsertMapping.mutateAsync({
        provider: 'airbnb',
        body: {
          connection_id: selectedConnId,
          external_listing_id: selectedListingId,
        },
      });
      toast({ kind: 'success', title: 'Airbnb listing mapped' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleActivate() {
    setErr(null);
    try {
      await activate.mutateAsync('airbnb');
      toast({ kind: 'success', title: 'Airbnb channel activated' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleDisable() {
    const ok = await askConfirm({
      title: 'Disable Airbnb channel?',
      body: 'ARI pushes will stop. You can re-enable at any time.',
      destructive: true,
      confirmLabel: 'Disable',
    });
    if (!ok) return;
    setErr(null);
    try {
      await disable.mutateAsync('airbnb');
      toast({ kind: 'success', title: 'Airbnb channel disabled' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleSync() {
    setErr(null);
    try {
      await sync.mutateAsync('airbnb');
      toast({ kind: 'success', title: 'Sync initiated' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleTest() {
    if (!selectedConnId) return;
    setErr(null);
    try {
      const result = await testConn.mutateAsync(selectedConnId);
      const conn = result.data as ChannelConnection;
      toast({ kind: 'success', title: 'Test passed', message: `Status: ${conn.status}` });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  const connOptions = connectedConnections.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  const listingOptions = listings.map((l) => ({
    value: l.id,
    label: l.title,
  }));

  const selectedConn = connectedConnections.find((c) => c.id === selectedConnId);

  return (
    <div className="space-y-5">
      {/* Account selection */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Airbnb account
        </h4>

        {/* Which account is in use is information a read-only caller needs, so
            the read-only path keeps the value and drops the control — the same
            "absent, not disabled" shape as SettingsTab. */}
        {connectedConnections.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              {canWrite ? (
                <Select
                  label="Select account"
                  options={connOptions}
                  value={selectedConnId}
                  onChange={(e) => {
                    setSelectedConnId(e.target.value);
                    setSelectedListingId('');
                  }}
                  placeholder="Select account…"
                />
              ) : (
                <ReadOnlyValue
                  label="Account"
                  value={connOptions.find((o) => o.value === selectedConnId)?.label ?? selectedConnId}
                  empty="None selected"
                />
              )}
            </div>
            {selectedConn && <ConnectionStatusBadge status={selectedConn.status} />}
          </div>
        )}

        {canWrite && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={createLink.isPending}
            onClick={handleConnectNew}
          >
            + Connect new account
          </Button>
        )}

        {canWrite && (
          <div>
            <button
              type="button"
              onClick={() => setShowSettings((s) => !s)}
              aria-expanded={showSettings}
              className="inline-flex items-center gap-1 text-xs text-muted hover:text-foreground"
            >
              {showSettings ? (
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              Channel settings
            </button>
            <p className="mt-1 text-xs text-muted">
              Applies only to the new Airbnb account you connect with the button above.
            </p>

            {showSettings && (
              <div className="mt-3 space-y-3 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Select
                    label="Min stay type"
                    options={MIN_STAY_TYPE_OPTIONS}
                    value={minStayType}
                    onChange={(e) =>
                      setMinStayType(e.target.value as NonNullable<AirbnbChannelSettings['min_stay_type']>)
                    }
                  />
                  <Select
                    label="Booking amount"
                    options={BOOKING_AMOUNT_OPTIONS}
                    value={bookingAmountSettings}
                    onChange={(e) =>
                      setBookingAmountSettings(
                        e.target.value as NonNullable<AirbnbChannelSettings['booking_amount_settings']>,
                      )
                    }
                  />
                </div>

                <Checkbox
                  label="Deduct co-host payout commission"
                  checked={cohostPayoutCalculations}
                  onChange={(e) => setCohostPayoutCalculations(e.target.checked)}
                />

                <Checkbox
                  label="Send Airbnb booking email notifications"
                  checked={sendEmailNotifications}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSendEmailNotifications(checked);
                    if (!checked) setEmailError(null);
                  }}
                />

                {sendEmailNotifications && (
                  <Input
                    label="Notification email"
                    type="email"
                    required
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    error={emailError ?? undefined}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {polling && (
          <p className="text-sm text-muted">
            Waiting for Airbnb authorisation… finish the flow in the opened tab.
          </p>
        )}
        {pollTimedOut && (
          <p className="text-sm text-[var(--ollio-warning)]">
            Still pending — finish authorisation in the opened tab, then refresh.
          </p>
        )}
      </div>

      {/* Listing mapping */}
      {selectedConnId && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Map listing
          </h4>
          {canWrite ? (
            <>
              <Select
                label="Airbnb listing"
                options={listingOptions}
                value={selectedListingId}
                onChange={(e) => setSelectedListingId(e.target.value)}
                placeholder="Select listing…"
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={upsertMapping.isPending}
                onClick={handleMap}
              >
                {mapping ? 'Update mapping' : 'Map listing'}
              </Button>
            </>
          ) : (
            // Bare id until the listings query resolves — see the note in
            // BookingComConnect: "Not mapped" during load is a lie.
            <ReadOnlyValue
              label="Airbnb listing"
              value={listingOptions.find((o) => o.value === selectedListingId)?.label ?? selectedListingId}
              empty="Not mapped"
            />
          )}
        </div>
      )}

      {/* Channel actions */}
      {mapping && canWrite && (
        <div className="flex flex-wrap gap-2">
          {!mapping.enabled && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={activate.isPending}
              onClick={handleActivate}
            >
              Activate
            </Button>
          )}
          {mapping.enabled && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              loading={disable.isPending}
              onClick={handleDisable}
            >
              Disable
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={sync.isPending}
            onClick={handleSync}
          >
            Sync
          </Button>
          {selectedConnId && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={testConn.isPending}
              onClick={handleTest}
            >
              Test
            </Button>
          )}
        </div>
      )}

      {err && (
        <p role="alert" className="text-sm text-destructive">
          {err}
        </p>
      )}

      {recentErrors.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Recent errors
          </h4>
          <ul className="space-y-1.5">
            {recentErrors.map((ev) => (
              <li
                key={ev.id}
                className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-xs text-destructive"
              >
                <span className="font-semibold">{ev.event_type}</span>
                {' · '}
                {new Date(ev.occurred_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ConnectionStatusBadge({ status }: { status: ChannelConnection['status'] }) {
  if (status === 'active') return <Badge variant="success" size="sm" dot>Active</Badge>;
  if (status === 'error') return <Badge variant="danger" size="sm">Error</Badge>;
  if (status === 'pending_auth') return <Badge variant="warning" size="sm">Pending auth</Badge>;
  return <Badge variant="neutral" size="sm">Inactive</Badge>;
}
